import { useMemo, useState } from 'react';

import { createColumnHelper, getCoreRowModel, getFilteredRowModel, useReactTable } from '@tanstack/react-table';
import { useParams } from 'react-router-dom';

import { FileDeletionType } from '../../../../../../../../../backend/src/types/types';
import { SettingsHeader } from '../../components/settings-header';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetDeletedFiles } from '@/api/queries/useGetDeletedFiles';
import formatDate from '@/helpers/formatDate';
import { downloadDeletionBatchReport } from '@/helpers/report-utils';
import { Icon } from '@/new/components/icon';
import { Badge } from '@/new/components/shadcn/badge';
import { Button } from '@/new/components/shadcn/button';
import { ScrollableTable } from '@/new/components/table/scrollable-table';

interface FlattenedDeletion {
  id: string;
  filename: string;
  deletedAt: string;
  deletedBy: string;
  type: FileDeletionType;
}

const columnHelper = createColumnHelper<FlattenedDeletion>();

export function DeletionDetail() {
  const { matterId, deletionId } = useParams();
  if (!matterId || !deletionId) return null;

  const [globalFilter, setGlobalFilter] = useState('');
  const { data: deletionHistory, isLoading } = useGetDeletedFiles(matterId);

  // Find the specific deletion
  const deletion = useMemo(() => {
    return deletionHistory?.find((d) => d.id === deletionId);
  }, [deletionHistory, deletionId]);

  // Flatten the deletion files into rows with full metadata
  const flattenedDeletions = useMemo(() => {
    if (!deletion) return [];

    return deletion.fileDeletion.map((file, index) => ({
      id: `${deletion.id}-${index}`,
      filename: file.filename,
      deletedAt: deletion.createdAt,
      deletedBy: deletion.createdBy?.email ?? 'Unknown',
      type: deletion.type,
    }));
  }, [deletion]);

  const fileCount = deletion?.fileDeletion?.length ?? 0;

  const columns = useMemo(
    () => [
      columnHelper.accessor('filename', {
        header: () => <span>Filename</span>,
        cell: (info) => (
          <div className="flex items-center gap-2">
            <Icon className="lucide--file h-3.5 w-3.5 text-gray-400" />
            <span className="text-sm text-gray-800">{info.getValue()}</span>
          </div>
        ),
      }),
      columnHelper.accessor('type', {
        header: () => <span>Deletion Type</span>,
        cell: (info) => {
          const type = info.getValue();
          const isStaged = type === FileDeletionType.Staged;
          return (
            <Badge variant={isStaged ? 'secondary' : 'default'} className="text-xs">
              <Icon className={`${isStaged ? 'lucide--clock' : 'lucide--send'} mr-1 h-3 w-3`} />
              {isStaged ? 'Before Submission' : 'After Submission'}
            </Badge>
          );
        },
      }),
      columnHelper.accessor('deletedBy', {
        header: () => <span>Deleted By</span>,
        cell: (info) => <span className="text-sm text-gray-600">{info.getValue()}</span>,
      }),
      columnHelper.accessor('deletedAt', {
        header: () => <span>Date</span>,
        cell: (info) => <span className="text-sm text-gray-600">{formatDate(info.getValue(), true, true)}</span>,
      }),
    ],
    [],
  );

  const table = useReactTable<FlattenedDeletion>({
    data: flattenedDeletions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      globalFilter,
    },
  });

  if (isLoading) {
    return (
      <>
        <SettingsHeader title="Deletion Details" subheading="Loading deletion details..." />
        <div className="py-8 text-center">
          <Icon className="lucide--loader-2 h-6 w-6 animate-spin text-gray-400" />
        </div>
      </>
    );
  }

  if (!deletion) {
    return (
      <>
        <SettingsHeader title="Deletion Details" subheading="The requested deletion could not be found" />
        <div className="py-8 text-center text-sm text-gray-500">Deletion not found.</div>
      </>
    );
  }

  return (
    <>
      <SettingsHeader
        title="Deletion Details"
        subheading={
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Icon className="lucide--calendar h-3.5 w-3.5" />
            <span>{formatDate(deletion.createdAt, true, true)}</span>
            {deletion.createdBy?.email && (
              <>
                <span className="text-gray-300">|</span>
                <Icon className="lucide--user h-3.5 w-3.5" />
                <span>{deletion.createdBy.email}</span>
              </>
            )}
          </div>
        }
        action={
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              trackCustomEvent(TrackingEventsV2.FileHistoryDownloadDeletionBatchReport, { matterId });
              deletion && downloadDeletionBatchReport(deletion, deletionId);
            }}
            disabled={fileCount === 0}
          >
            <Icon className="lucide--download h-3.5 w-3.5" />
            Download Report
          </Button>
        }
      />

      {/* List of deleted files */}
      {fileCount > 0 ? (
        <ScrollableTable
          table={table}
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          plural="deleted files"
          loading={isLoading}
          className="p-0"
        />
      ) : (
        <div className="py-8 text-center text-sm text-gray-500">No files in this deletion.</div>
      )}
    </>
  );
}
